import {
  Challenge,
  CreateChallengeInput,
  UpdateChallengeInput,
  ChallengeOperationResult,
  ChallengeApiResponse,
  ChallengeDeleteResponse,
  ChallengeUser,
  ChallengeUsersApiResponse,
  AssignUsersResponse,
  UnassignUsersResponse,
} from "../types/challenge";
import { ApiService } from "./apiService";
import { fetchAllPages, unwrapPaginated } from "../utils/paginationHelper";
import { failure } from "./serviceHelpers";
import { logger } from "../utils/logger";

export class ChallengeService {
  // CREATE - Add a new challenge
  static async createChallenge(
    challengeData: CreateChallengeInput
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.post("/challenges", challengeData);
      const result = await ApiService.handleResponse<ChallengeApiResponse>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected create challenge API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // READ - Get all challenges across every page of the paginated endpoint
  static async getChallenges(): Promise<Challenge[] | null> {
    try {
      return await fetchAllPages<Challenge>("/challenges");
    } catch (err: any) {
      logger.error("Error fetching challenges:", err);
      return null;
    }
  }

  // READ - Get a single challenge by ID
  static async getChallengeById(
    challengeId: number
  ): Promise<Challenge | null> {
    try {
      const response = await ApiService.get(`/challenges/${challengeId}`);
      const result = await ApiService.handleResponse<ChallengeApiResponse>(
        response
      );

      if (result.data) {
        return result.data;
      }

      logger.error("Unexpected challenge API response format:", result);
      return null;
    } catch (err: any) {
      logger.error("Error fetching challenge:", err);
      return null;
    }
  }

  // UPDATE - Update challenge information
  static async updateChallenge(
    challengeId: number,
    updates: UpdateChallengeInput
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.patch(
        `/challenges/${challengeId}`,
        updates
      );
      const result = await ApiService.handleResponse<ChallengeApiResponse>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected update challenge API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // DELETE - Delete challenge
  static async deleteChallenge(
    challengeId: number
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.delete(`/challenges/${challengeId}`);
      const result = await ApiService.handleResponse<ChallengeDeleteResponse>(
        response
      );

      if (result.data && result.data.deleted) {
        return { success: true };
      }

      return { success: false, error: "Delete operation failed" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // USER ASSIGNMENT - Get users assigned to challenge
  static async getChallengeUsers(
    challengeId: number,
    page: number = 1
  ): Promise<{
    users: ChallengeUser[];
    total: number;
    page: number;
    limit: number;
  } | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      const response = await ApiService.get(
        `/challenges/${challengeId}/users?${params.toString()}`
      );
      const result = await ApiService.handleResponse<ChallengeUsersApiResponse>(
        response
      );

      const paged = unwrapPaginated(result, "challenge users");
      if (!paged) return null;

      return {
        users: paged.items,
        total: paged.total,
        page: paged.page,
        limit: paged.limit,
      };
    } catch (err: any) {
      logger.error("Error fetching challenge users:", err);
      return null;
    }
  }

  // USER ASSIGNMENT - Get every user assigned to a challenge. The endpoint
  // paginates; the assignment modal wants the whole set, which is small.
  static async getAllChallengeUsers(
    challengeId: number
  ): Promise<ChallengeUser[] | null> {
    try {
      return await fetchAllPages<ChallengeUser>(
        `/challenges/${challengeId}/users?per_page=100`
      );
    } catch (err: any) {
      logger.error("Error fetching challenge users:", err);
      return null;
    }
  }

  // USER ASSIGNMENT - Assign users to challenge
  static async assignUsersToChallenge(
    challengeId: number,
    userIds: number[]
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.post(
        `/challenges/${challengeId}/assign-users`,
        { user_ids: userIds }
      );
      const result = await ApiService.handleResponse<AssignUsersResponse>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data as any };
      }

      logger.error("Unexpected assign users API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // USER ASSIGNMENT - Unassign users from challenge
  static async unassignUsersFromChallenge(
    challengeId: number,
    userIds: number[]
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.post(
        `/challenges/${challengeId}/unassign-users`,
        { user_ids: userIds }
      );
      const result = await ApiService.handleResponse<UnassignUsersResponse>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data as any };
      }

      logger.error("Unexpected unassign users API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // PROGRAM ASSIGNMENT - Assign program to challenge
  static async assignProgramToChallenge(
    challengeId: number,
    programId: number
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.post(
        `/challenges/${challengeId}/assign-program/${programId}`,
        {}
      );
      const result = await ApiService.handleResponse<any>(response);

      return { success: true, data: result };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // PROGRAM ASSIGNMENT - Unassign program from challenge
  static async unassignProgramFromChallenge(
    challengeId: number,
    programId: number
  ): Promise<ChallengeOperationResult> {
    try {
      const response = await ApiService.post(
        `/challenges/${challengeId}/unassign-program/${programId}`,
        {}
      );
      const result = await ApiService.handleResponse<any>(response);

      return { success: true, data: result };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }
}
